import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { 
  Search, Eye, Trash2, CheckCircle, Clock, XCircle, 
  Loader2, Mail, ShieldCheck, Info, DollarSign
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "react-hot-toast"; // Import Toaster here
import "../styles/AdminQuote.css";

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("quotes")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) toast.error("Database sync failed");
    else setQuotes(data || []);
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    const toastId = toast.loading(`Updating to ${newStatus}...`);
    
    const { error } = await supabase
      .from("quotes")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      toast.error("Update failed", { id: toastId });
    } else {
      toast.success(`Quote ${newStatus} successfully`, { id: toastId });
      setQuotes(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q));
      setSelectedQuote(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanent delete?")) return;
    const { error } = await supabase.from("quotes").delete().eq("id", id);
    if (!error) {
      setQuotes(prev => prev.filter(q => q.id !== id));
      toast.success("Record deleted");
    }
  };

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          q.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const status = q.status || 'New';
    if (statusFilter === "Active") return matchesSearch && (status === "New" || status === "Pending");
    if (statusFilter === "All") return matchesSearch;
    return matchesSearch && status === statusFilter;
  });

  return (
    <div className="rni-admin-container">
      {/* THIS MAKES TOASTS APPEAR IN FRONT */}
      <Toaster 
        position="top-center" 
        containerStyle={{ zIndex: 99999 }} 
      />

      <header className="rni-header">
        <div className="header-left">
          <h1>Quote Management</h1>
          <p>Processing business risk valuations</p>
        </div>
      </header>

      <div className="rni-controls">
        <div className="search-wrapper">
          <Search size={18} />
          <input placeholder="Search clients..." onChange={(e) => setSearchTerm(e.target.value)} />
        </div>

        <div className="tabs">
          {["Active", "Approved", "Declined", "All"].map(t => (
            <button 
              key={t} 
              className={statusFilter === t ? "tab active" : "tab"}
              onClick={() => setStatusFilter(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="table-card">
        <table className="rni-main-table">
          <thead>
            <tr>
              <th>Client Entity</th>
              <th>Product</th>
              <th>Valuation</th>
              <th>Status</th>
              <th style={{textAlign: 'right'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="loading-state"><Loader2 className="rni-spin" /></td></tr>
            ) : filteredQuotes.map(q => (
              <tr key={q.id}>
                <td className="entity-cell">
                  <strong>{q.entity_name}</strong>
                  <span>{q.email}</span>
                </td>
                <td><span className="type-pill">{q.coverage_type}</span></td>
                <td className="val-cell">Ksh {Number(q.valuation).toLocaleString()}</td>
                <td><span className={`badge ${q.status?.toLowerCase() || 'new'}`}>{q.status || 'New'}</span></td>
                <td style={{textAlign: 'right'}}>
                  <div className="row-actions">
                    <button onClick={() => setSelectedQuote(q)} className="view-btn">
                      <Eye size={14} /> View Details
                    </button>
                    <button onClick={() => handleDelete(q.id)} className="del-btn">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selectedQuote && (
          <div className="glass-overlay" onClick={() => setSelectedQuote(null)}>
            <motion.div 
              className="premium-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close-btn" onClick={() => setSelectedQuote(null)}>
                <XCircle size={24} />
              </button>

              <div className="modal-header-info">
                <span className="modal-subtitle">Quote ID: #{selectedQuote.id.slice(0,8)}</span>
                <h2>{selectedQuote.entity_name}</h2>
              </div>

              <div className="modal-grid">
                <div className="grid-item">
                  <label><Mail size={12}/> Contact</label>
                  <p>{selectedQuote.email}</p>
                </div>
                <div className="grid-item">
                  <label><ShieldCheck size={12}/> Product</label>
                  <p>{selectedQuote.coverage_type}</p>
                </div>
                <div className="grid-item">
                  <label><DollarSign size={12}/> Valuation</label>
                  <p className="val-high">Ksh {Number(selectedQuote.valuation).toLocaleString()}</p>
                </div>
              </div>

              <div className="modal-desc">
                <label><Info size={12}/> Risk Requirements</label>
                <div className="desc-inner">
                  {selectedQuote.requirements || "No specific requirements provided."}
                </div>
              </div>

              <div className="modal-footer-btns">
                <button className="action-b approve" onClick={() => handleStatusChange(selectedQuote.id, 'Approved')}>
                  <CheckCircle size={18}/> Approve
                </button>
                <button className="action-b pending" onClick={() => handleStatusChange(selectedQuote.id, 'Pending')}>
                  <Clock size={18}/> Pending
                </button>
                <button className="action-b decline" onClick={() => handleStatusChange(selectedQuote.id, 'Declined')}>
                  <XCircle size={18}/> Decline
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminQuotes;